<template>
  <!-- ───────────────── SIDEBAR ───────────────── -->
  <v-navigation-drawer v-model="drawer" :width="300" class="sidebar-drawer">
    <!-- Logo -->
    <div class="sidebar-logo">
      <a href="/" class="logo-link">
        <div class="logo-icon-wrap">
          <v-img height="auto" width="auto" :src="logoSrc" contain></v-img>
        </div>
        <div>
          <div class="logo-text">Quatro</div>
          <div class="logo-sub">Yönetim Sistemi</div>
        </div>
      </a>
    </div>

    <v-divider class="sidebar-divider" />

    <!-- Menü araması -->
    <div class="menu-search-wrap">
      <v-text-field
        v-model="query"
        class="menu-search"
        placeholder="Menüde ara..."
        prepend-inner-icon="mdi-magnify"
        variant="solo"
        density="compact"
        flat
        hide-details
        clearable
        autocomplete="off"
        @keydown.esc="query = ''"
      />
    </div>

    <!-- Menü — v-list zorunlu, v-list-group içinde çalışsın diye -->
    <v-list v-model:opened="listOpened" density="compact" nav class="sidebar-nav">
      <div v-if="isSearching && !filteredItems.length" class="menu-empty">
        "{{ query }}" için sonuç bulunamadı
      </div>
      <template v-for="item in filteredItems" :key="item.title">
        <!-- Alt menüsüz öğe -->
        <v-list-item
          v-if="!item.child"
          :href="item.route"
          :active="isActive(item.route)"
          active-class="nav-active"
          rounded="lg"
          class="nav-list-item"
        >
          <template v-slot:prepend>
            <div class="nav-icon-box">
              <v-icon :icon="item.icon" size="15" />
            </div>
          </template>
          <v-list-item-title class="nav-item-title">{{
            item.title
          }}</v-list-item-title>
        </v-list-item>

        <!-- Alt menülü öğe -->
        <v-list-group v-if="item.child" :value="item.title">
          <template v-slot:activator="{ props }">
            <v-list-item v-bind="props" rounded="lg" class="nav-list-item">
              <template v-slot:prepend>
                <div class="nav-icon-box">
                  <v-icon :icon="item.icon" size="15" />
                </div>
              </template>
              <v-list-item-title class="nav-item-title">{{
                item.title
              }}</v-list-item-title>
            </v-list-item>
          </template>

          <v-list-item
            v-for="child in item.child"
            :key="child.title"
            :href="child.route"
            :active="isActive(child.route)"
            active-class="nav-active"
            rounded="lg"
            class="nav-list-item nav-child-item"
          >
            <template v-slot:prepend>
              <div class="child-dot-wrap">
                <div class="child-dot" />
              </div>
            </template>
            <v-list-item-title class="nav-item-title">{{
              child.title
            }}</v-list-item-title>
          </v-list-item>
        </v-list-group>
      </template>
    </v-list>

    <!-- Footer: Kullanıcı -->
    <template v-slot:append>
      <v-divider class="sidebar-divider" />
      <div class="sidebar-footer">
        <v-menu location="top end" :offset="8">
          <template v-slot:activator="{ props }">
            <div class="user-card" v-bind="props">
              <div class="user-avatar">{{ getInitials(user.fullName) }}</div>
              <div class="user-info">
                <div class="user-name">{{ user.fullName }}</div>
                <div class="user-role">Kullanıcı</div>
              </div>
              <v-icon icon="mdi-dots-horizontal" size="16" class="more-icon" />
            </div>
          </template>
          <v-list class="user-menu-list" elevation="4">
            <v-list-item
              class="user-menu-item"
              rounded="lg"
              @click="navigate('/password_change')"
            >
              <template v-slot:prepend
                ><v-icon icon="mdi-account-key-outline" size="16"
              /></template>
              <v-list-item-title>{{
                $t("sifremi_degistir")
              }}</v-list-item-title>
            </v-list-item>
            <v-list-item
              class="user-menu-item user-menu-item--danger"
              rounded="lg"
              @click="logOutFunc"
            >
              <template v-slot:prepend
                ><v-icon icon="mdi-logout" size="16"
              /></template>
              <v-list-item-title>{{ $t("cikis_yap") }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </template>
  </v-navigation-drawer>

  <!-- ───────────────── APP BAR ───────────────── -->
  <v-app-bar class="top-appbar" :elevation="0" app>
    <v-app-bar-nav-icon @click.stop="drawClick" class="nav-toggle" />

    <!-- Dil Seçici -->
    <v-select
      v-model="$i18n.locale"
      :items="languages"
      item-title="text"
      item-value="value"
      variant="outlined"
      hide-details
      density="compact"
      class="lang-select"
      prepend-inner-icon="mdi-translate"
      @update:model-value="changeLanguage"
    />

    <v-spacer />

    <!-- Bildirim zili (yalnızca araç sahibi) -->
    <notification-bell />

    <!-- Kullanıcı Dropdown -->
    <v-menu location="bottom end" :offset="8">
      <template v-slot:activator="{ props: menu }">
        <v-tooltip
          location="bottom"
          :text="$t('kullanici_islemleri_icin_tiklayiniz')"
        >
          <template v-slot:activator="{ props: tooltip }">
            <div class="topbar-user" v-bind="mergeProps(menu, tooltip)">
              <div class="topbar-avatar">{{ getInitials(user.fullName) }}</div>
              <span class="topbar-name">{{ user.fullName }}</span>
              <v-icon
                icon="mdi-chevron-down"
                size="14"
                class="topbar-chevron"
              />
            </div>
          </template>
        </v-tooltip>
      </template>
      <v-list class="user-menu-list" elevation="4">
        <v-list-item
          class="user-menu-item"
          rounded="lg"
          @click="navigate('/password_change')"
        >
          <template v-slot:prepend
            ><v-icon icon="mdi-account-key-outline" size="16"
          /></template>
          <v-list-item-title>{{ $t("sifremi_degistir") }}</v-list-item-title>
        </v-list-item>
        <v-list-item
          class="user-menu-item user-menu-item--danger"
          rounded="lg"
          @click="logOutFunc"
        >
          <template v-slot:prepend
            ><v-icon icon="mdi-logout" size="16"
          /></template>
          <v-list-item-title>{{ $t("cikis_yap") }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
  </v-app-bar>
</template>

<script>
import { mergeProps } from "vue";
import logoSideBar from "../../../public/assets/img/quatro_white_logo.svg";
import { mapActions } from "pinia";
import { useUserStore } from "../../stores/UserStore";
import { useI18nStore } from "../../stores/i18nStore";
import NotificationBell from "./NotificationBell.vue";
import { stopHubNow } from "@/utils/notificationHub";

// Her sayfa kendi layout'unu (ve bu menüyü) yeniden oluşturduğu için açık grupları, çekmece
// durumunu ve kaydırma konumunu sayfa geçişlerinde burada hatırlıyoruz.
const STORE_KEY = "quatro_sidebar_state";
const readState = () => {
  try {
    return JSON.parse(sessionStorage.getItem(STORE_KEY)) || {};
  } catch (e) {
    return {};
  }
};
const memory = readState();
// Türkçe karakterleri ve büyük/küçük harfi yok sayarak karşılaştırma ("ı" ile "i", "ş" ile "s" eşleşir)
const normalize = (text) =>
  String(text || "")
    .toLocaleLowerCase("tr")
    .replace(/ı/g, "i")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
const saveState = () => {
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify(memory));
  } catch (e) {
    /* depolama kapalıysa sadece bellekte kalır */
  }
};

export default {
  components: { NotificationBell },
  props: ["menuItems", "title", "user"],
  data() {
    return {
      query: "",
      opened: Array.isArray(memory.opened) ? [...memory.opened] : [],
      drawer: typeof memory.drawer === "boolean" ? memory.drawer : true,
      logoSrc: logoSideBar,
      languages: [
        { text: "Türkçe", value: "tr" },
        { text: "English", value: "en" },
        { text: "Deutsch", value: "de" },
      ],
    };
  },
  computed: {
    normQuery() {
      return normalize(this.query).trim();
    },
    isSearching() {
      return !!this.normQuery;
    },
    // Aramada: başlığı eşleşen öğe/grup görünür; grup başlığı eşleşirse tüm alt öğeleri, yoksa
    // yalnızca eşleşen alt öğeleri listelenir.
    filteredItems() {
      const items = this.menuItems || [];
      const q = this.normQuery;
      if (!q) return items;
      return items
        .map((item) => {
          if (normalize(item.title).includes(q)) return item;
          if (!item.child) return null;
          const child = item.child.filter((c) => normalize(c.title).includes(q));
          return child.length ? { ...item, child } : null;
        })
        .filter(Boolean);
    },
    // Aramada eşleşen gruplar açık gösterilir; kullanıcının kendi açık/kapalı durumu bozulmaz.
    listOpened: {
      get() {
        return this.isSearching
          ? this.filteredItems.filter((i) => i.child).map((i) => i.title)
          : this.opened;
      },
      set(value) {
        if (!this.isSearching) this.opened = value;
      },
    },
  },
  watch: {
    // Akordeon: aynı anda tek grup açık kalır (menü uzayıp kalabalıklaşmasın)
    opened(value, old) {
      if (value.length > 1) {
        const added = value.find((v) => !(old || []).includes(v));
        this.opened = [added ?? value[value.length - 1]];
        return;
      }
      memory.opened = [...value];
      saveState();
    },
    drawer(value) {
      memory.drawer = value;
      saveState();
    },
    menuItems() {
      this.openActiveGroup();
    },
    $route() {
      this.openActiveGroup();
    },
  },
  mounted() {
    this.openActiveGroup();
    this.$nextTick(() => {
      const el = this.navScroller();
      if (el && memory.scrollTop) el.scrollTop = memory.scrollTop;
    });
  },
  beforeUnmount() {
    const el = this.navScroller();
    if (el) {
      memory.scrollTop = el.scrollTop;
      saveState();
    }
  },
  methods: {
    ...mapActions(useUserStore, ["logout"]),
    navScroller() {
      return this.$el?.parentElement?.querySelector?.(".sidebar-drawer .v-navigation-drawer__content") ||
        document.querySelector(".sidebar-drawer .v-navigation-drawer__content");
    },
    // Aktif sayfanın bulunduğu grup kapalıysa aç (diğer açık gruplara dokunmaz).
    openActiveGroup() {
      const group = (this.menuItems || []).find(
        (item) => item.child && item.child.some((c) => this.isActive(c.route)),
      );
      if (group && !this.opened.includes(group.title)) {
        this.opened = [...this.opened, group.title];
      }
    },
    ...mapActions(useI18nStore, ["setLocale"]),
    mergeProps,
    navigate(route) {
      this.$router.replace(route);
    },
    changeLanguage() {
      this.setLocale(this.$i18n.locale);
      this.$router.go();
    },
    drawClick() {
      this.drawer = !this.drawer;
    },
    isActive(route) {
      // Menü rotaları "/#/yol" biçiminde, router yolu ise "/yol".
      return this.$route?.path === String(route || "").replace(/^\/#/, "");
    },
    getInitials(name) {
      if (!name) return "?";
      const parts = name.trim().split(" ");
      return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : parts[0].slice(0, 2).toUpperCase();
    },
    async logOutFunc() {
      await stopHubNow();
      const response = await this.logout();
      if (response.status) this.navigate("/login");
    },
  },
};
</script>

<style scoped>
/* ══════════════════════════════════════════════
   SIDEBAR
══════════════════════════════════════════════ */
.sidebar-drawer {
  background: linear-gradient(180deg, #03275c 0%, #021a3f 55%, #011230 100%) !important;
  border-right: none !important;
  box-shadow: 4px 0 24px rgba(2, 18, 48, 0.25) !important;
}
.sidebar-divider {
  border-color: rgba(255, 255, 255, 0.08) !important;
  margin: 0 16px !important;
}

/* ── Logo ── */
.sidebar-logo {
  padding: 22px 18px 18px;
}
.logo-link {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
}
.logo-icon-wrap {
  width: 34%;
  height: auto;
  padding: 4%;
  border-radius: 14px;
  background: linear-gradient(160deg, #1b95ff, #0a2a9c);
  box-shadow: 0 6px 16px rgba(7, 132, 242, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.logo-text {
  font-size: 17px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.3px;
}
.logo-sub {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
  margin-top: 1px;
}

/* ── Menü araması ── */
.menu-search-wrap {
  padding: 10px 12px 0;
}
.menu-search :deep(.v-field) {
  background: rgba(255, 255, 255, 0.07) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  color: #ffffff;
  font-size: 13px;
}
.menu-search :deep(.v-field:focus-within) {
  background: rgba(255, 255, 255, 0.11) !important;
  border-color: rgba(96, 165, 250, 0.6);
}
.menu-search :deep(input) {
  color: #ffffff;
}
.menu-search :deep(input::placeholder) {
  color: rgba(255, 255, 255, 0.5);
  opacity: 1;
}
.menu-search :deep(.v-icon) {
  color: rgba(255, 255, 255, 0.6) !important;
  opacity: 1;
}
.menu-empty {
  padding: 14px 10px;
  font-size: 12.5px;
  color: rgba(255, 255, 255, 0.55);
  text-align: center;
}

/* ── Nav ── */
.sidebar-nav {
  padding: 8px 12px !important;
  background: transparent !important;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.18) transparent;
}
.sidebar-nav::-webkit-scrollbar {
  width: 6px;
}
.sidebar-nav::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.16);
  border-radius: 6px;
}

.nav-list-item {
  position: relative;
  color: rgba(255, 255, 255, 0.78) !important;
  margin-bottom: 2px !important;
  min-height: 38px !important;
  border: 1px solid transparent !important;
  transition: background 0.18s, color 0.18s, transform 0.18s !important;
}
.nav-list-item:hover {
  background: rgba(255, 255, 255, 0.06) !important;
  color: #ffffff !important;
}
.nav-list-item:hover .nav-icon-box {
  background: rgba(96, 165, 250, 0.2);
  color: #8ec1ff;
}
.nav-active {
  background: linear-gradient(90deg, rgba(59, 130, 246, 0.28), rgba(59, 130, 246, 0.08)) !important;
  color: #ffffff !important;
  font-weight: 600;
}
.nav-active::before {
  content: "";
  position: absolute;
  left: 0;
  top: 22%;
  bottom: 22%;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: #60a5fa;
  box-shadow: 0 0 10px rgba(96, 165, 250, 0.8);
}
.nav-active .nav-icon-box {
  background: linear-gradient(160deg, #2f7cf6, #1d4ed8) !important;
  color: #ffffff !important;
  box-shadow: 0 4px 10px rgba(29, 78, 216, 0.4);
}

.nav-icon-box {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.07);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.7);
  flex-shrink: 0;
  margin-right: 10px;
  transition: background 0.18s, color 0.18s;
}
.nav-item-title {
  font-size: 13px !important;
  font-weight: 500 !important;
  letter-spacing: 0.1px;
  white-space: break-spaces !important;
  line-height: 1.3 !important;
}

/* Alt menü: sol kılavuz çizgisi */
:deep(.v-list-group__items) {
  margin: 2px 0 6px 27px;
  padding-left: 8px;
  border-left: 1px solid rgba(255, 255, 255, 0.1);
}
.nav-child-item {
  padding-left: 8px !important;
  min-height: 32px !important;
}
.child-dot-wrap {
  width: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
}
.child-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.35);
  transition: background 0.18s, transform 0.18s;
}
.nav-child-item:hover .child-dot {
  background: #8ec1ff;
  transform: scale(1.3);
}
.nav-child-item.nav-active .child-dot {
  background: #60a5fa;
  box-shadow: 0 0 8px rgba(96, 165, 250, 0.9);
}
.nav-child-item.nav-active::before {
  display: none;
}
.nav-child-item .nav-item-title {
  font-size: 12.5px !important;
  font-weight: 400 !important;
}

/* v-list-group chevron */
:deep(.v-list-group__header .v-list-item__append .v-icon) {
  color: rgba(255, 255, 255, 0.55) !important;
  font-size: 18px !important;
}

/* ── Kullanıcı Kartı ── */
.sidebar-footer {
  padding: 12px 14px 16px;
}
.user-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.09);
  cursor: pointer;
  transition: background 0.18s, border-color 0.18s;
}
.user-card:hover {
  background: rgba(255, 255, 255, 0.11);
  border-color: rgba(255, 255, 255, 0.16);
}
.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 11px;
  background: linear-gradient(160deg, #ffc83d, #f59e0b);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #3b2a00;
  flex-shrink: 0;
  letter-spacing: 0.5px;
}
.user-info {
  flex: 1;
  min-width: 0;
}
.user-name {
  font-size: 13.5px;
  font-weight: 600;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.user-role {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.55);
  margin-top: 1px;
}
.more-icon {
  color: rgba(255, 255, 255, 0.6) !important;
}

/* ── Dropdown Menü ── */
.user-menu-list {
  background: #0d1f42 !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 14px !important;
  padding: 6px !important;
  min-width: 200px !important;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.35) !important;
}
.user-menu-item {
  color: rgba(255, 255, 255, 0.8) !important;
  min-height: 40px !important;
  font-size: 13px !important;
  margin-bottom: 2px !important;
}
.user-menu-item :deep(.v-list-item__prepend .v-icon) {
  color: rgba(255, 255, 255, 0.65) !important;
  margin-right: 10px !important;
}
.user-menu-item:hover {
  background: rgba(255, 255, 255, 0.08) !important;
  color: #ffffff !important;
}
.user-menu-item--danger:hover {
  background: rgba(239, 68, 68, 0.15) !important;
  color: #fca5a5 !important;
}

/* ══════════════════════════════════════════════
   APP BAR
══════════════════════════════════════════════ */
.top-appbar {
  background: rgba(255, 255, 255, 0.85) !important;
  backdrop-filter: saturate(180%) blur(12px);
  -webkit-backdrop-filter: saturate(180%) blur(12px);
  border-bottom: 1px solid rgba(15, 23, 42, 0.06) !important;
  box-shadow: 0 2px 16px rgba(15, 23, 42, 0.04) !important;
  min-height: 64px !important;
}
.nav-toggle {
  color: #475569 !important;
  border-radius: 12px !important;
}
.nav-toggle:hover {
  background: #eef3fb;
}

/* Dil select */
.lang-select {
  max-width: 170px;
}
:deep(.lang-select .v-field) {
  border-radius: 999px !important;
  font-size: 13px !important;
  background: #f1f5fb !important;
}
:deep(.lang-select .v-field__outline) {
  color: transparent !important;
}
:deep(.lang-select .v-field--focused .v-field__outline) {
  color: #3b82f6 !important;
}

/* Kullanıcı alanı */
.topbar-user {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 12px 5px 5px;
  border-radius: 999px;
  cursor: pointer;
  margin-right: 8px;
  background: #f1f5fb;
  border: 1px solid transparent;
  transition: background 0.18s, border-color 0.18s, box-shadow 0.18s;
}
.topbar-user:hover {
  background: #e7eefb;
  border-color: #d5e2f7;
  box-shadow: 0 4px 12px rgba(30, 64, 175, 0.08);
}
.topbar-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(160deg, #2f7cf6, #1d4ed8);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
  letter-spacing: 0.5px;
}
.topbar-name {
  font-size: 13.5px;
  font-weight: 600;
  color: #1e293b;
}
.topbar-chevron {
  color: #64748b !important;
}

/* Dar ekran: app bar taşmasın */
@media (max-width: 599.98px) {
  .lang-select {
    max-width: 130px;
  }
  .topbar-name {
    display: none;
  }
  .topbar-user {
    margin-right: 0;
    padding-right: 8px;
  }
}
</style>
